/**
 * Connection allowlists by target shop business type.
 * Keep in sync with server/src/utils/businessTypeConnectionRules.js
 */

export const normBT = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const BT_ALIASES = {
  rawmaterialshop: "rawmaterialShop",
  rawmaterial: "rawmaterialShop",
  manufacturershop: "manufacturerShop",
  manufacturer: "manufacturerShop",
  industrial: "manufacturerShop",
  wholesellershop: "wholesellerShop",
  wholeseller: "wholesellerShop",
  wholesale: "wholesellerShop",
  retailershop: "retailerShop",
  retailer: "retailerShop",
  retail: "retailerShop",
  homemadeshop: "homemadeShop",
  homemade: "homemadeShop",
  usedshop: "usedShop",
  used: "usedShop",
  useditems: "usedShop",
  secondhand: "usedShop",
  "2ndhand": "usedShop",
  gemshop: "gemShop",
  gem: "gemShop",
  gems: "gemShop",
  jewellery: "gemShop",
  farmingshop: "farmingShop",
  farming: "farmingShop",
  farm: "farmingShop",
  businesspropertycompany: "businessPropertyCompany",
  businessproperty: "businessPropertyCompany",
  property: "businessPropertyCompany",
  transportshop: "transportShop",
  transport: "transportShop",
  transportation: "transportShop",
  services: "services",
  service: "services",
  foodshop: "foodShop",
  food: "foodShop",
  recycledshop: "recycledShop",
  recycle: "recycledShop",
  recycled: "recycledShop",
  generalpublic: "generalPublic",
  jobscompany: "jobsCompany",
  company: "jobsCompany",
  jobs: "jobsCompany",
  job: "jobsCompany",
};

export const canonicalizeBusinessType = (value) => {
  const n = normBT(value);
  if (!n) return "";
  return BT_ALIASES[n] || value;
};

/** Raw Material Select Shop — only these sender profiles */
export const RAW_MATERIAL_ALLOWED_CONNECT_TYPES = [
  "rawmaterialShop",
  "manufacturerShop",
  "businessPropertyCompany",
  "transportShop",
  "services",
  "foodShop",
  "recycledShop",
];

/** Industrial / Manufacturer Select Shop — only these sender profiles */
export const MANUFACTURER_ALLOWED_CONNECT_TYPES = [
  "manufacturerShop",
  "rawmaterialShop",
  "wholesellerShop",
  "businessPropertyCompany",
  "transportShop",
  "recycledShop",
  "services",
  "foodShop",
];

/** Wholeseller Select Shop — only these sender profiles */
export const WHOLESELLER_ALLOWED_CONNECT_TYPES = [
  "wholesellerShop",
  "manufacturerShop",
  "retailerShop",
  "homemadeShop",
  "usedShop",
  "gemShop",
  "businessPropertyCompany",
  "transportShop",
  "recycledShop",
  "services",
  "farmingShop",
  "foodShop",
];

/** Retailer Select Shop — only these sender profiles */
export const RETAILER_ALLOWED_CONNECT_TYPES = [
  "retailerShop",
  "wholesellerShop",
  "generalPublic",
  "businessPropertyCompany",
  "recycledShop",
  "services",
  "foodShop",
];

/** Jobs Company Select Shop — only these sender profiles */
export const JOBS_COMPANY_ALLOWED_CONNECT_TYPES = [
  "jobsCompany",
  "generalPublic",
  "retailerShop",
  "businessPropertyCompany",
  "transportShop",
  "recycledShop",
  "services",
  "foodShop",
];

/** Gems & Jewellery Select Shop — only these sender profiles */
export const GEM_SHOP_ALLOWED_CONNECT_TYPES = [
  "gemShop",
  "generalPublic",
  "wholesellerShop",
  "businessPropertyCompany",
  "transportShop",
  "recycledShop",
  "services",
  "foodShop",
];

/** 2nd Hand / Used Items Select Shop — only these sender profiles */
export const USED_SHOP_ALLOWED_CONNECT_TYPES = [
  "usedShop",
  "generalPublic",
  "wholesellerShop",
  "businessPropertyCompany",
  "transportShop",
  "recycledShop",
  "services",
  "foodShop",
];

/** Homemade Select Shop — only these sender profiles */
export const HOMEMADE_SHOP_ALLOWED_CONNECT_TYPES = [
  "homemadeShop",
  "generalPublic",
  "wholesellerShop",
  "businessPropertyCompany",
  "transportShop",
  "recycledShop",
  "services",
  "foodShop",
];

/** Farming Select Shop — only these sender profiles */
export const FARMING_SHOP_ALLOWED_CONNECT_TYPES = [
  "farmingShop",
  "generalPublic",
  "wholesellerShop",
  "businessPropertyCompany",
  "transportShop",
  "recycledShop",
  "services",
  "foodShop",
];

/** 2nd Hand Vehicles Select Shop — only these sender profiles */
export const USED_VEHICLE_SHOP_ALLOWED_CONNECT_TYPES = [
  "usedVehicleShop",
  "generalPublic",
  "wholesellerShop",
  "businessPropertyCompany",
  "transportShop",
  "recycledShop",
  "services",
  "foodShop",
];

export const CONNECTION_ALLOWLIST_BY_TARGET = {
  rawmaterialShop: RAW_MATERIAL_ALLOWED_CONNECT_TYPES,
  manufacturerShop: MANUFACTURER_ALLOWED_CONNECT_TYPES,
  wholesellerShop: WHOLESELLER_ALLOWED_CONNECT_TYPES,
  retailerShop: RETAILER_ALLOWED_CONNECT_TYPES,
  jobsCompany: JOBS_COMPANY_ALLOWED_CONNECT_TYPES,
  gemShop: GEM_SHOP_ALLOWED_CONNECT_TYPES,
  usedShop: USED_SHOP_ALLOWED_CONNECT_TYPES,
  homemadeShop: HOMEMADE_SHOP_ALLOWED_CONNECT_TYPES,
  farmingShop: FARMING_SHOP_ALLOWED_CONNECT_TYPES,
  usedVehicleShop: USED_VEHICLE_SHOP_ALLOWED_CONNECT_TYPES,
};

export const BUSINESS_TYPE_LABELS = {
  rawmaterialShop: "Raw Material",
  manufacturerShop: "Industrial / Manufacturer",
  wholesellerShop: "Wholeseller",
  retailerShop: "Retailer",
  homemadeShop: "Homemade",
  usedShop: "2nd Hand / Used Items",
  gemShop: "Gems & Jewellery",
  businessPropertyCompany: "Property",
  transportShop: "Transportation",
  services: "Services Provider",
  farmingShop: "Farming",
  foodShop: "Food",
  recycledShop: "Recycle",
  jobsCompany: "Jobs Company",
  generalPublic: "General Profile",
  usedVehicleShop: "2nd Hand / Used Vehicles",
};

export const CONNECTION_ALLOWLIST_HINTS = {
  rawmaterialShop:
    "Raw Material, Industrial/Manufacturer, Property, Transportation, Services Provider, Food, Recycle",
  manufacturerShop:
    "Industrial/Manufacturer, Raw Material, Wholeseller, Property, Transportation, Recycle, Services Provider, Food",
  wholesellerShop:
    "Wholeseller, Industrial/Manufacturer, Retailer, Homemade, 2nd Hand/Used Items, Gems & Jewellery, Property, Transportation, Recycle, Services Provider, Farming, Food",
  retailerShop:
    "Retailer, Wholeseller, General Public, Property, Recycle, Services Provider, Food",
  jobsCompany:
    "Jobs Company, General Public, Retailer, Property, Transportation, Recycle, Services Provider, Food",
  gemShop:
    "Gems & Jewellery, General Public, Wholeseller, Property, Transportation, Recycle, Services Provider, Food",
  usedShop:
    "2nd Hand / Used Items, General Public, Wholeseller, Property, Transportation, Recycle, Services Provider, Food",
  homemadeShop:
    "Homemade, General Public, Wholeseller, Property, Transportation, Recycle, Services Provider, Food",
  farmingShop:
    "Farming, General Public, Wholeseller, Property, Transportation, Recycle, Services Provider, Food",
  usedVehicleShop:
    "2nd Hand / Used Vehicles, General Public, Wholeseller, Property, Transportation, Recycle, Services Provider, Food",
};

export const getAllowedConnectTypesForTarget = (targetBusinessType) => {
  const canonical = canonicalizeBusinessType(targetBusinessType);
  const list = CONNECTION_ALLOWLIST_BY_TARGET[canonical];
  return Array.isArray(list) ? list : null;
};

export const isBusinessTypeInAllowlist = (businessType, allowlist) => {
  if (!Array.isArray(allowlist) || allowlist.length === 0) return true;
  const n = normBT(canonicalizeBusinessType(businessType));
  return allowlist.some((t) => normBT(t) === n);
};

export const getBusinessTypeLabel = (businessType) => {
  const canonical = canonicalizeBusinessType(businessType);
  return BUSINESS_TYPE_LABELS[canonical] || businessType || "";
};

export const getConnectionAllowlistHint = (targetBusinessType) => {
  const canonical = canonicalizeBusinessType(targetBusinessType);
  return CONNECTION_ALLOWLIST_HINTS[canonical] || "allowed shop types";
};

export const getConnectionRestrictionMessage = (targetBusinessType) => {
  const label = getBusinessTypeLabel(targetBusinessType) || "This";
  const hint = getConnectionAllowlistHint(targetBusinessType);
  return `This ${label} shop can only connect with ${hint}.`;
};
